import { Checkbox, Select, Tooltip } from "antd";
import { useMemo, useRef } from "react";
import { useDndScrolling } from "react-dnd-scrolling";
import { useSelector } from "react-redux";
import { getFirstLetters } from "utils";
import ProviderCell from "./components/ProviderCell";
import useBoardRunner from "../../pages/app/Board/components/BoardRunner/useBoardRunner";
import BoardRunnerCell from "./components/BoradRunnerCell";
import DroppableCell from "./components/DroppableCell";

const useVerticalSchedular = ({
  autoScroll,
  resources,
  unprocessedEvents,
  components = {},
  handleDropOnEventCb,
  providers,
  allowDropOnSchedular,
  handleResourceDropCb,
  isDropDownActive,
  boardDate,
  facilityId,
  selectedFacility,
  handleRefreshProvidersList,
  isBoardRunner,
  providerProps,
}) => {
  const calendarContainerRef = useRef();
  const { Component, props = {} } = components?.resourceHeader;
  const timezone = useSelector((state) => state.user.user.timezone);

  const {
    dropRef,
    boardRunners,
    handleAssignBoardRunner,
    handleUnassignBoardRunner,
    getBoardRunnersApiLoading,
    unassignBoardRunnerApiLoading,
    boardAssignments,
    getBoardAssignmentsApiLoading,
    handleAssignBoardAssignment,
    handleUnassignBoardAssignment,
    assignBoardAssignmentApiLoading,
    unassignBoardAssignmentApiLoading,
    useAssignmentDrop,
    isProvider,
  } = useBoardRunner({ boardDate, facilityId, handleRefreshProvidersList });

  const mainColumnHeaders = [
    {
      title: "",
      dataIndex: "section",
      key: "section",
      width: 200,
      render: (text, record) => {
        const currentResource = resources?.find(
          (r) => r.name?.toUpperCase() === text,
        );
        const providerOptions =
          providers?.map((p) => ({
            label: p.name,
            value: p.id,
          })) || [];

        if (record?.isBoardRunnerRow) {
          const runner = boardRunners?.[0];
          // Children — full-width content
          //masla idhr hy
          const children = (
            <BoardRunnerCell
              isNormal={true}
              dropRef={dropRef}
              runner={runner}
              providers={providerProps?.providers}
              providerOptions={providerOptions}
              currentResource={currentResource}
              handleUnassignBoardRunner={handleUnassignBoardRunner}
              getBoardRunnersApiLoading={getBoardRunnersApiLoading}
              renderProviderSelectDropdown={renderProviderSelectDropdown}
              unassignBoardRunnerApiLoading={unassignBoardRunnerApiLoading}
              assignments={boardAssignments}
              getBoardAssignmentsApiLoading={getBoardAssignmentsApiLoading}
              handleAssignBoardAssignment={handleAssignBoardAssignment}
              handleUnassignBoardAssignment={handleUnassignBoardAssignment}
              assignBoardAssignmentApiLoading={assignBoardAssignmentApiLoading}
              unassignBoardAssignmentApiLoading={
                unassignBoardAssignmentApiLoading
              }
              assignmentDrop={useAssignmentDrop}
              selectedFacility={selectedFacility}
              isBoardRunner={isBoardRunner}
              isProvider={isProvider}
            />
          );

          return {
            children,
            props: { colSpan: 1 },
          };
        }

        // normal rooms column header
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <strong>{text}</strong>
            {isDropDownActive &&
              record?.provider == "Add Provider" &&
              renderRoomProviderSelectDropdown(
                "room-provider",
                currentResource,
                providerOptions,
              )}
          </div>
        );
      },
    },
    {
      id: "1",
      title: "Start",
      dataIndex: "start",
      key: "start",
      width: 80,
    },
    {
      id: "2",
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      width: 100,
      render: (text) => <strong>{text}</strong>,
    },
    {
      id: "3",
      title: "Surgeon",
      dataIndex: "surgeonName",
      key: "surgeonName",
      width: 220,
    },
    {
      id: "4",
      title: "Procedure",
      dataIndex: "title",
      key: "title",
      width: 350,
    },
    {
      id: "5",
      title: "Anesthesia Provider",
      dataIndex: "providerId",
      width: 200,
    },
    {
      id: "6",
      title: "Patient Name",
      dataIndex: "patientName",
      key: "patientName",
      width: 200,
    },
    {
      id: "7",
      title: "QA",
      dataIndex: "qa",
      key: "qa",
      width: 80,
    },
    {
      id: "8",
      title: "Docs",
      dataIndex: "docs",
      key: "docs",
      width: 80,
    },
  ];

  // Total columns count (used for colSpan)
  const TOTAL_COLUMNS = mainColumnHeaders.length;

  // ---- Important: patch the first column's render return to set correct colSpan at runtime ----
  // AntD render closures evaluate later, so we can overwrite colSpan here by wrapping the original render.
  // Replace the first column's render with a wrapper that sets proper colSpan when isBoardRunnerRow.
  const originalFirstRender = mainColumnHeaders[0].render;
  mainColumnHeaders[0].render = (text, record, index) => {
    const result = originalFirstRender(text, record, index);
    // If original render returned an object (children + props)
    if (
      result &&
      typeof result === "object" &&
      ("children" in result || "props" in result)
    ) {
      // If it's BoardRunner row, set full width
      if (record?.isBoardRunnerRow) {
        return {
          children: result.children,
          props: { colSpan: TOTAL_COLUMNS },
        };
      }
      // else return original children and no special props
      return result;
    }
    // If it returned a React node, return object with no colSpan
    if (record?.isBoardRunnerRow) {
      return {
        children: result,
        props: { colSpan: TOTAL_COLUMNS },
      };
    }
    return result;
  };

  const renderRoomProviderSelectDropdown = (
    selectionType,
    currentResource,
    providerOptions,
  ) => (
    <Select
      size="small"
      style={{ width: 120 }}
      placeholder={currentResource?.provider?.name || "Add Provider"}
      value={currentResource?.provider?.id}
      options={providerOptions}
      onClick={(e) => e.stopPropagation()} // prevent expand toggle
      onChange={(newProviderId) => {
        const providerName =
          providerOptions.find((p) => p.value === newProviderId)?.label || "";
        if (selectionType == "board-runner") {
          const selectedProvider = {
            providerId: newProviderId,
            providerName,
          };
          handleAssignBoardRunner(selectedProvider);
        } else {
          handleResourceDropCb({
            providerId: newProviderId,
            providerName,
            ...currentResource,
          });
        }
      }}
    />
  );

  const renderProviderSelectDropdown = (
    providerProps,
    selectedData = [],
    type = null,
    disabled = false,
  ) => (
    <Select
      mode="multiple"
      placeholder="Select Providers"
      style={{ width: 200 }}
      value={selectedData}
      open={undefined} // allow normal open behavior
      options={providerProps?.map((provider) => ({
        value: provider.id,
        label: provider.name,
      }))}
      maxTagCount={1}
      maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
      removeIcon={disabled ? null : undefined}
      /* 🔒 Prevent selection */
      onSelect={() => {
        if (disabled) return false;
      }}
      /* 🔒 Prevent unselect */
      onDeselect={() => {
        if (disabled) return false;
      }}
      /* 🔒 Final guard (nothing changes) */
      onChange={(values, options) => {
        if (disabled) return;
        const selectedProviders = {
          providerIds: values,
          providerNames: options.map((o) => o.label),
        };
        type
          ? handleAssignBoardAssignment({ selectedProviders, type })
          : handleAssignBoardRunner(selectedProviders);
      }}
    />
  );

  const roomNameHeaders = useMemo(() => {
    return resources?.map((resource) => {
      // Filter out events for this room
      const resourceProcedures = unprocessedEvents
        ?.filter((item) => item.resourceId === resource.id)
        ?.map((item) => {
          let duration = 0;

          if (item.start && item.end) {
            const startTime = new Date(item.start);
            const endTime = new Date(item.end);
            duration = (endTime - startTime) / (1000 * 60);
          }

          return {
            ...item,
            duration,
          };
        });

      // If no data, create one empty placeholder row
      const finalData =
        resourceProcedures && resourceProcedures.length > 0
          ? resourceProcedures
          : [
              {
                id: `${resource.id}-empty`,
                resourceId: resource.id,
                providerId: null,
                providerName: null,
                title: "",
                start: null,
                end: null,
                duration: null,
                isEmpty: true, // mark it for logic later if needed
              },
            ];

      const providerName = resource?.provider?.name;
      const initials = providerName ? getFirstLetters(providerName) : "";

      return {
        key: resource.id,
        section: resource.name?.toUpperCase(),
        provider: providerName ? initials : "Add Provider",
        data: finalData,
        grouped: true,
      };
    });
  }, [resources, unprocessedEvents]);

  const childColumns = [
    {
      title: "Provider",
      dataIndex: "provider",
      key: "provider",
      width: 200, // Match section column width
      render: (text, record, index) => {
        const sectionData = roomNameHeaders.find(
          (r) => r.section === record.section,
        );
        const rowSpan = index === 0 ? sectionData?.data?.length || 1 : 0;

        const currentResource = resources?.find(
          (resource) => record.resourceId === resource.id,
        );

        const providerOptions =
          providers?.map((p) => ({
            label: p.name,
            value: p.id,
          })) || [];

        const currentProviderId = record?.provider?.id;
        const currentProviderName = record?.provider?.name;

        const providerExists = providerOptions.some(
          (opt) => opt.value === currentProviderId,
        );
        if (!providerExists && currentProviderId && currentProviderName) {
          providerOptions.unshift({
            label: currentProviderName,
            value: currentProviderId,
          });
        }

        return {
          children: (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                justifyContent: "center",
                alignItems: "center",
              }}>
              <ProviderCell
                text={text}
                record={record}
                currentResource={currentResource}
                handleResourceDropCb={handleResourceDropCb}
                allowDropOnSchedular={allowDropOnSchedular}
                props={props}
                Component={Component}
              />
            </div>
          ),
          props: { rowSpan },
        };
      },
    },

    ...mainColumnHeaders.slice(1).map((col) => ({
      ...col,
      render: (value, record) => {
        // Hide all these cells when the row is the BoardRunner row
        if (record?.isBoardRunnerRow) {
          return {
            children: null,
            props: { colSpan: 0 },
          };
        }

        // original render logic (keeps behavior intact)
        if (record?.isEmpty) return null;

        // original render logic preserved exactly
        let renderedContent;

        switch (col.dataIndex) {
          case "providerId": {
            const providerOptions =
              providers?.map((p) => ({
                label: p.name,
                value: p.id,
              })) || [];

            const providerExists = providerOptions.some(
              (opt) => opt.value === value,
            );

            if (!providerExists && value && record?.providerName) {
              providerOptions.unshift({
                label: record?.providerName,
                value,
              });
            }

            renderedContent = (
              <Select
                style={{ width: "165px" }}
                value={value}
                options={providerOptions}
                onClick={(e) => e.stopPropagation()}
                onChange={(newProviderId) =>
                  handleDropOnEventCb({
                    providerId: newProviderId,
                    providerName: record?.providerName,
                    id: record?.id,
                    newRoomId: record?.resourceId,
                    zohoMirrorId: null,
                  })
                }
              />
            );
            break;
          }

          case "start": {
            const date = new Date(record?.start);
            const formattedTime = date.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: false,
              timeZone: timezone,
            });
            renderedContent = formattedTime;
            break;
          }

          case "title":
          case "surgeonName":
            if (!value) return null;

            renderedContent = (
              <Tooltip
                title={value}
                placement="top"
                mouseEnterDelay={0.3}
                overlayStyle={{ maxWidth: 400 }}>
                <div
                  style={{
                    pointerEvents: "auto",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: "default",
                  }}>
                  {value}
                </div>
              </Tooltip>
            );
            break;

          case "patientName": {
            const fullName =
              `${record?.patientFirstName ?? ""} ${record?.patientLastName ?? ""}`.trim();
            renderedContent = (
              <Tooltip
                title={fullName}
                placement="top"
                mouseEnterDelay={0.3}
                overlayStyle={{ maxWidth: 400 }}>
                <div
                  style={{
                    pointerEvents: "auto",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: "default",
                  }}>
                  {fullName}
                </div>
              </Tooltip>
            );
            break;
          }

          case "qa": {
            renderedContent = (
              <Checkbox checked={record?.qaCompleted === true} />
            );
          }

          case "docs": {
            renderedContent = (
              <Checkbox checked={record?.documentUpload === true} />
            );
          }

          default:
            renderedContent = value;
        }

        return (
          <DroppableCell
            record={record}
            handleDropOnEventCb={handleDropOnEventCb}
            allowDropOnSchedular={allowDropOnSchedular}>
            {renderedContent}
          </DroppableCell>
        );
      },
    })),
  ];

  const boardRunnerRow = {
    key: "boardrunner",
    section: "BoardRunner:",
    provider: null,
    isBoardRunnerRow: true,
  };

  const tableData = [boardRunnerRow, ...roomNameHeaders];

  useDndScrolling(autoScroll && calendarContainerRef);

  return {
    calendarContainerRef,
    mainColumnHeaders,
    roomNameHeaders,
    childColumns,
    tableData,
  };
};

export default useVerticalSchedular;
